import { raidQueueService, type QueuedStream } from '../lib/raidQueue';
import { TwitchStreamsService } from '../lib/twitchStreams';
import { twitchService } from '../lib/twitch';
import Swal from 'sweetalert2';
import './RaidQueueComponent.css';

export class RaidQueueComponent {
  private button!: HTMLElement;
  private popout!: HTMLElement;
  private queueList!: HTMLElement;
  private streamsService: TwitchStreamsService;
  private refreshInterval: number | null = null;

  constructor(container: HTMLElement) {
    const elements = this.getRequiredElements(container);
    Object.assign(this, elements);

    const accessToken = twitchService.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    this.streamsService = new TwitchStreamsService(
      import.meta.env.VITE_TWITCH_CLIENT_ID,
      accessToken
    );

    // Override RaidQueueService methods to dispatch events
    const originalAddToQueue = raidQueueService.addToQueue;
    raidQueueService.addToQueue = (stream) => {
      originalAddToQueue.call(raidQueueService, stream);
      this.updateBadge();
      document.dispatchEvent(new CustomEvent('queueupdate'));
    };

    const originalRemoveFromQueue = raidQueueService.removeFromQueue;
    raidQueueService.removeFromQueue = (userId) => {
      originalRemoveFromQueue.call(raidQueueService, userId);
      this.updateBadge();
      document.dispatchEvent(new CustomEvent('queueupdate'));
    };

    const originalMoveToPosition = raidQueueService.moveToPosition;
    raidQueueService.moveToPosition = (userId, newIndex) => {
      originalMoveToPosition.call(raidQueueService, userId, newIndex);
      this.updateBadge();
      document.dispatchEvent(new CustomEvent('queueupdate'));
    };

    this.initializeEventListeners();
    this.startAutoCleanup();
  }

  private startAutoCleanup(): void {
    // Clean up old raid history every hour
    this.refreshInterval = window.setInterval(() => {
      raidQueueService.cleanupOldHistory();
    }, 60 * 60 * 1000); // 1 hour
  }

  private getRequiredElements(container: HTMLElement) {
    const button = container.querySelector('.raid-queue-button');
    const popout = document.querySelector('.raid-queue-popout');
    const queueList = document.querySelector('.raid-queue-list');

    if (!button || !popout || !queueList) {
      throw new Error('Required raid queue elements not found');
    }

    return {
      button: button as HTMLElement,
      popout: popout as HTMLElement,
      queueList: queueList as HTMLElement
    };
  }

  private initializeEventListeners(): void {
    // Add drag and drop handlers
    this.queueList.addEventListener('dragstart', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('raid-queue-item') && target.getAttribute('data-is-event') !== 'true') {
        target.classList.add('dragging');
        e.dataTransfer?.setData('text/plain', target.getAttribute('data-user-id') || '');
      } else {
        e.preventDefault();
      }
    });

    this.queueList.addEventListener('dragend', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('raid-queue-item')) {
        target.classList.remove('dragging');
      }
    });

    this.queueList.addEventListener('dragover', (e) => {
      e.preventDefault();
      const draggingItem = this.queueList.querySelector('.dragging');
      if (!draggingItem) return;

      const target = e.target as HTMLElement;
      const queueItem = target.closest('.raid-queue-item');
      if (!queueItem || 
          queueItem === draggingItem || 
          queueItem.getAttribute('data-is-event') === 'true') return;

      const rect = queueItem.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;
      const moveAfter = e.clientY > midY;

      if (moveAfter) {
        queueItem.after(draggingItem);
      } else {
        queueItem.before(draggingItem);
      }
    });

    this.queueList.addEventListener('drop', (e) => {
      e.preventDefault();
      const userId = e.dataTransfer?.getData('text/plain');
      if (!userId) return;

      const items = Array.from(this.queueList.querySelectorAll('.raid-queue-item'));
      const newIndex = items.findIndex(item => item.classList.contains('dragging'));
      if (newIndex !== -1) {
        raidQueueService.moveToPosition(userId, newIndex);
      }
    });

    // Toggle popout
    this.button.addEventListener('click', () => {
      // Close teammates popout if it's open
      const teammatesPopout = document.querySelector('.teammates-popout');
      if (teammatesPopout && teammatesPopout.classList.contains('active')) {
        teammatesPopout.classList.remove('active');
      }
      
      this.popout.classList.toggle('active');
      this.renderQueueList();
    });

    // Close button handler
    const closeButton = this.popout.querySelector('.raid-queue-popout-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.popout.classList.remove('active');
      });
    }

    // Listen for queue updates
    document.addEventListener('queueupdate', () => {
      if (this.popout.classList.contains('active')) {
        this.renderQueueList();
      }
    });

    // Handle clicks in queue list
    this.queueList.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      
      // Handle r3dlabs event container footer clicks
      if (target.classList.contains('r3dlabs-event-container-footer') && target.classList.contains('live')) {
        e.preventDefault();
        e.stopPropagation();
        const userId = target.getAttribute('data-user-id');
        if (userId) {
          const stream = raidQueueService.getQueue().find(s => s.userId === userId);
          if (stream) {
            await this.handleRaidClick(stream);
          }
        }
        return;
      }

      // Handle raid button clicks
      if (target.classList.contains('raid-queue-item-raid')) {
        e.preventDefault();
        e.stopPropagation();
        const queueItem = target.closest('.raid-queue-item');
        const userId = queueItem?.getAttribute('data-user-id');
        if (userId) {
          const stream = raidQueueService.getQueue().find(s => s.userId === userId);
          if (stream) {
            await this.handleRaidClick(stream);
          }
        }
        return;
      }

      // Handle remove button clicks
      if (target.classList.contains('raid-queue-item-remove')) {
        const queueItem = target.closest('.raid-queue-item');
        const userId = queueItem?.getAttribute('data-user-id');
        if (userId) {
          raidQueueService.removeFromQueue(userId);
          this.renderQueueList();
        }
      }
    });
  }

  private async handleRaidClick(stream: QueuedStream): Promise<void> {
    const result = await Swal.fire({
      title: 'Start Raid?',
      html: `Are you sure you want to raid <strong>${stream.userName}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Start Raid!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#9147ff',
      cancelButtonColor: '#6e7681',
      background: '#18181b',
      color: '#ffffff',
      width: '360px'
    });

    if (result.isConfirmed) {
      try {
        await this.streamsService.raidChannel(stream.userId);
        
        await Swal.fire({
          title: 'Raid Started!',
          html: `Successfully started raid to <strong>${stream.userName}</strong>`,
          icon: 'success',
          timer: 2000,
          timerProgressBar: true,
          confirmButtonColor: '#9147ff',
          background: '#18181b',
          color: '#ffffff',
          width: '360px'
        });

        // Mark as raided and remove from queue
        raidQueueService.markAsRaided(stream.userId);
        raidQueueService.removeFromQueue(stream.userId);
      } catch (error) {
        console.error('Failed to start raid:', error);
        
        const errorMessage = error instanceof Error ? error.message : 'Unable to start the raid. Please try again.';
        await Swal.fire({
          title: 'Unable to Start Raid',
          html: `<div style="text-align: left; margin-top: 1rem;">${errorMessage}</div>`,
          icon: 'error',
          confirmButtonColor: '#9147ff',
          background: '#18181b',
          color: '#ffffff',
          width: '360px'
        });
      }
    }
  }

  private updateBadge(): void {
    const queue = raidQueueService.getQueue();
    const badge = this.button.querySelector('.raid-queue-badge') as HTMLElement;
    if (badge) {
      badge.textContent = queue.length.toString();
      badge.style.display = queue.length > 0 ? 'flex' : 'none';
    }
  }

  private renderQueueList(): void {
    const queue = raidQueueService.getQueue();
    const eventStreams = queue.filter(stream => stream.eventName);
    const standardStreams = queue.filter(stream => !stream.eventName);
    
    // Combine the sections with their labels
    const sections = [];
    
    if (eventStreams.length > 0) {
      sections.push(`<div class="raid-queue-section-label">R3dLabs Raid Train</div>`);
      sections.push(eventStreams.map(stream => `
      <div class="r3dlabs-event-container" data-user-id="${stream.userId}">
        <div class="r3dlabs-event-container-content">
          <div class="row">
            <div class="col-2">
              <img class="raid-queue-item-avatar" src="${stream.profileImageUrl}" alt="${stream.userName}">
            </div>
            <div class="col-10">
              <div class="raid-queue-item-name">${stream.userName}</div>
              <div class="raid-queue-item-event">${stream.eventName}</div>
            </div>
          </div>
        </div>
        <div class="r3dlabs-event-container-footer ${stream.isLive ? 'live' : 'waiting'}" 
             ${stream.isLive ? `data-user-id="${stream.userId}"` : ''}>
          ${stream.isLive ? 'Click To Raid Now' : 'Waiting For Go-Live'}
        </div>
      </div>`).join(''));
    }
    
    if (standardStreams.length > 0) {
      sections.push(`<div class="raid-queue-section-label">Manually Added</div>`);
      sections.push(standardStreams.map((stream, index) => `
      <div class="raid-queue-item" 
           draggable="true"
           data-user-id="${stream.userId}"
           data-is-event="false">
        <div class="raid-queue-item-position">${index + 1}</div>
        <img class="raid-queue-item-avatar" src="${stream.profileImageUrl}" alt="${stream.userName}">
        <div class="raid-queue-item-info">
          <div class="raid-queue-item-name">${stream.userName}</div>
          ${stream.activeSlotId ? `
            <div class="raid-queue-item-status ${stream.isLive ? 'online' : 'offline'}">
              ${stream.isLive ? 'Live' : 'Offline'}
            </div>
          ` : ''}
          ${(!stream.activeSlotId || stream.isLive) ? `
            <button class="raid-queue-item-raid">Raid Now</button>
          ` : ''}
        </div>
        <button class="raid-queue-item-remove" title="Remove from queue">×</button>
      </div>`).join(''));
    }
    
    this.queueList.innerHTML = sections.join('');
  }

  public async initialize(): Promise<void> {
    // Set initial badge state
    const queue = raidQueueService.getQueue();
    const badge = this.button.querySelector('.raid-queue-badge') as HTMLElement;
    if (badge) {
      badge.textContent = queue.length.toString();
      badge.style.display = queue.length > 0 ? 'flex' : 'none';
    }
  }

  public destroy(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }
}
